import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { Board, BoardDetail, BoardSummary, BoardSettings, CreateBoardRequest, UpdateBoardRequest } from '../types';
import { BoardsApi } from '../api';

/**
 * Query keys for boards
 */
export const boardKeys = {
  all: ['boards'] as const,
  lists: () => [...boardKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...boardKeys.lists(), filters] as const,
  details: () => [...boardKeys.all, 'detail'] as const,
  detail: (id: string) => [...boardKeys.details(), id] as const,
};

/**
 * Hook to get all boards
 */
export function useBoards(
  boardsApi: BoardsApi,
  options?: Omit<UseQueryOptions<BoardSummary[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<BoardSummary[], Error>({
    queryKey: boardKeys.lists(),
    queryFn: () => boardsApi.getBoards(),
    ...options,
  });
}

/**
 * Hook to get a specific board with full details
 */
export function useBoard(
  boardsApi: BoardsApi,
  boardId: string,
  options?: Omit<UseQueryOptions<BoardDetail, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<BoardDetail, Error>({
    queryKey: boardKeys.detail(boardId),
    queryFn: () => boardsApi.getBoard(boardId),
    enabled: !!boardId,
    ...options,
  });
}

/**
 * Hook to create a board
 */
export function useCreateBoard(
  boardsApi: BoardsApi,
  options?: UseMutationOptions<Board, Error, { request?: CreateBoardRequest; hubConnectionId?: string }>
) {
  const queryClient = useQueryClient();

  return useMutation<Board, Error, { request?: CreateBoardRequest; hubConnectionId?: string }>({
    mutationFn: ({ request, hubConnectionId }) => boardsApi.createBoard(request, hubConnectionId),
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate boards list
      queryClient.invalidateQueries({ queryKey: boardKeys.lists() });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    ...options,
  });
}

/**
 * Hook to update board settings
 */
export function useUpdateBoardSettings(
  boardsApi: BoardsApi,
  options?: UseMutationOptions<BoardSettings, Error, { boardId: string; settings: BoardSettings; hubConnectionId?: string }>
) {
  const queryClient = useQueryClient();

  return useMutation<BoardSettings, Error, { boardId: string; settings: BoardSettings; hubConnectionId?: string }>({
    mutationFn: ({ boardId, settings, hubConnectionId }) => boardsApi.updateBoardSettings(boardId, settings, hubConnectionId),
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate boards list and specific board
      queryClient.invalidateQueries({ queryKey: boardKeys.lists() });
      queryClient.invalidateQueries({ queryKey: boardKeys.detail(variables.boardId) });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    ...options,
  });
}

/**
 * Hook to update a board
 */
export function useUpdateBoard(
  boardsApi: BoardsApi,
  options?: UseMutationOptions<Board, Error, { boardId: string; request: UpdateBoardRequest; hubConnectionId?: string }>
) {
  const queryClient = useQueryClient();

  return useMutation<Board, Error, { boardId: string; request: UpdateBoardRequest; hubConnectionId?: string }>({
    mutationFn: ({ boardId, request, hubConnectionId }) => boardsApi.updateBoard(boardId, request, hubConnectionId),
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate boards list and specific board
      queryClient.invalidateQueries({ queryKey: boardKeys.lists() });
      queryClient.invalidateQueries({ queryKey: boardKeys.detail(variables.boardId) });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    ...options,
  });
}

/**
 * Hook to delete a board
 */
export function useDeleteBoard(
  boardsApi: BoardsApi,
  options?: UseMutationOptions<void, Error, { boardId: string; hubConnectionId?: string }>
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { boardId: string; hubConnectionId?: string }>({
    mutationFn: ({ boardId, hubConnectionId }) => boardsApi.deleteBoard(boardId, hubConnectionId),
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate boards list and remove specific board from cache
      queryClient.invalidateQueries({ queryKey: boardKeys.lists() });
      queryClient.removeQueries({ queryKey: boardKeys.detail(variables.boardId) });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    ...options,
  });
}

/**
 * Hook to archive a board
 */
export function useArchiveBoard(
  boardsApi: BoardsApi,
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (boardId) => boardsApi.archiveBoard(boardId),
    onSuccess: (data, boardId, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: boardKeys.lists() });
      queryClient.invalidateQueries({ queryKey: boardKeys.detail(boardId) });
      options?.onSuccess?.(data, boardId, onMutateResult, context);
    },
    ...options,
  });
}

/**
 * Hook to sync board connections
 */
export function useSyncConnections(
  boardsApi: BoardsApi,
  options?: UseMutationOptions<number, Error, { boardId: string; fullSync: boolean; hubConnectionId?: string }>
) {
  const queryClient = useQueryClient();

  return useMutation<number, Error, { boardId: string; fullSync: boolean; hubConnectionId?: string }>({
    mutationFn: ({ boardId, fullSync, hubConnectionId }) => boardsApi.syncConnections(boardId, fullSync, hubConnectionId),
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: boardKeys.detail(variables.boardId) });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    ...options,
  });
}

/**
 * Hook to toggle mute notifications
 */
export function useToggleMuteNotifications(
  boardsApi: BoardsApi,
  options?: UseMutationOptions<boolean, Error, { boardId: string; hubConnectionId?: string }>
) {
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, { boardId: string; hubConnectionId?: string }>({
    mutationFn: ({ boardId, hubConnectionId }) => boardsApi.toggleMuteNotifications(boardId, hubConnectionId),
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: boardKeys.detail(variables.boardId) });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    ...options,
  });
}
